/**
 * Контракт хранилища прогресса.
 *
 * Это ключевая точка расширения архитектуры: сегодня данные лежат
 * в Telegram CloudStorage, завтра тот же интерфейс реализует адаптер
 * поверх API-сервиса (лидерборды, синк между устройствами).
 * Приложение работает с интерфейсом и о смене реализации не узнает.
 *
 * Dependency Inversion: core зависит от абстракции, не от CloudStorage.
 */

export interface ProgressStore {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
  keys(): Promise<string[]>
}

/** Типобезопасная обёртка: пишем и читаем объекты, а не строки. */
export class JsonStore {
  constructor(private readonly store: ProgressStore) {}

  async get<T>(key: string, fallback: T): Promise<T> {
    const raw = await this.store.get(key)
    if (raw === null || raw === '') return fallback
    try {
      return JSON.parse(raw) as T
    } catch {
      // повреждённая запись не должна ронять приложение
      return fallback
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    await this.store.set(key, JSON.stringify(value))
  }

  async remove(key: string): Promise<void> {
    await this.store.remove(key)
  }

  async keys(): Promise<string[]> {
    return this.store.keys()
  }
}

/** Пространство ключей. Держим в одном месте, чтобы не разъезжались. */
export const KEYS = {
  profile: 'profile',
  settings: 'settings',
  gameStats: (gameId: string) => `stats.${gameId}`,
  dailyWorkout: (date: string) => `workout.${date}`,
  streak: 'streak',
  indexHistory: 'index.history',
} as const
